import { useEffect, useState } from 'react';
import { Button, Card, Container, Stack } from '@mui/material';

import Iconify from 'src/components/iconify';
// Import React-Quill and its styles
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  languages,
  quillFormats,
  quillModules,
  visibleLanguages,
} from 'src/config/quill-text-editor-settings';
import { LanguageTabs, TabPanel } from '../language-tabs';
import { useGetPickemRulesQuery } from '@/api/pages/getPageByKey.ts';
import { useCreatePageMutation } from '@/api/pages/createPage.ts';
import { PageType } from '@/@types/api.types.ts';
import { useToast } from '@/hooks/useToast.ts';
import { useUpdatePageMutation } from '@/api/pages/updatePage.ts';
import { Toast } from '@/components/Toast/index.tsx';
import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';

// Initialize default content state
const initialState: Record<string, string> = visibleLanguages.reduce(
  (acc: Record<string, string>, lang) => {
    acc[`content_${lang}`] = ''; // Now using string for HTML content
    return acc;
  },
  {}
);

export const PickemRulesEdit = () => {
  const { pickemId } = useAppSearchParams();

  const { toast, toastProps } = useToast({
    duration: 3000, // 3 seconds
    vertical: 'bottom',
    horizontal: 'right',
  });

  const [value, setValue] = useState(0);
  const [pageId, setPageId] = useState<number>(0);
  const [state, setState] = useState<Record<string, string>>(initialState);

  const { data: page, isLoading } = useGetPickemRulesQuery({ pickemId });
  const { mutateAsync: createPage, isPending: isPendingCreate } =
    useCreatePageMutation();
  const { mutateAsync: updatePage, isPending: isPendingUpdate } =
    useUpdatePageMutation();

  useEffect(() => {
    if (!page) return;

    setState({ ...initialState });
    setPageId(page.page_id);
    for (let lang of languages) {
      // Use the HTML content directly
      setState((prevState) => ({
        ...prevState,
        [`content_${lang}`]: page[`content_${lang}`] || '',
      }));
    }
  }, [page]);

  const handleChangeTabs = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleChange = (content: string, lang: string) => {
    setState((prevState) => ({
      ...prevState,
      [`content_${lang}`]: content,
    }));
  };

  const saveHandler = () => {
    let page = {} as PageType;
    for (let lang of languages) {
      // Already in HTML format, no conversion needed
      page[`content_${lang}`] = state[`content_${lang}`];
    }

    page.page_key = `pickem-${pickemId}`;

    if (!pageId) {
      return createPage(page)
        .then(() => toast('Page created'))
        .catch(() => toast(`Create page error`));
    }

    updatePage({ ...page, page_id: pageId })
      .then(() => toast('Page updated'))
      .catch(() => toast(`Update page error`));
  };

  return (
    <Container>
      <Stack direction='row' alignItems='right' mb={5}>
        <Button
          variant='contained'
          startIcon={<Iconify icon='eva:save-outline' />}
          onClick={() => saveHandler()}
          disabled={isPendingCreate || isPendingUpdate}
          sx={{ mr: 2 }}
        >
          {isPendingCreate || isPendingUpdate ? 'Saving...' : 'Save'}
        </Button>
      </Stack>

      <LanguageTabs value={value} onChange={handleChangeTabs} />
      {visibleLanguages.map((lang, index) => (
        <TabPanel key={lang.code} value={value} index={index}>
          <Card sx={{ p: 1 }}>
            {isLoading ? (
              'Loading...'
            ) : (
              <ReactQuill
                value={state[`content_${lang.code}`]}
                onChange={(content) => handleChange(content, lang.code)}
                modules={quillModules}
                formats={quillFormats}
                style={{ height: '400px' }}
              />
            )}
          </Card>
        </TabPanel>
      ))}

      <Toast {...toastProps} />
    </Container>
  );
};
